import React from 'react'
import { Link } from 'react-router-dom'
import { Row, Col, Badge } from 'reactstrap'
import { ErrorBoundary } from 'components'
import FieldsetItem from '../../../components/FieldsetItem'
import { ContractLineDetails } from 'types'

interface ContractLineSummaryProps {
  contractLineDetails: ContractLineDetails
}

const ContractLineSummary: React.FC<ContractLineSummaryProps> = ({
  contractLineDetails,
}) => {
  if (!contractLineDetails.contractLineId) return null

  const formatDate = (date: Date | null): string => {
    if (!date) return '-'
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const isExpired = contractLineDetails.expirationDate
    ? new Date(contractLineDetails.expirationDate) < new Date()
    : false

  const hasUnassignedAssets = contractLineDetails.unassignedAssets.length > 0

  const adminEmail = contractLineDetails.adminEmail

  const setUpAdminEmailLink =
    adminEmail && contractLineDetails.setupAdminEmailUserId
      ? `/css-admin/users/${adminEmail}/${contractLineDetails.setupAdminEmailUserId}`
      : null

  return (
    <ErrorBoundary>
      <Row>
        <Col sm={8}>
          <fieldset>
            <legend>Details</legend>
            <Row>
              <Col>
                <FieldsetItem title="Setup Admin">
                  {setUpAdminEmailLink ? (
                    <Link to={setUpAdminEmailLink}>{adminEmail || '-'}</Link>
                  ) : (
                    adminEmail || '-'
                  )}
                </FieldsetItem>
              </Col>
              <Col>
                <FieldsetItem
                  color="none"
                  data={formatDate(new Date(contractLineDetails.startDate))}
                  title="Start Date"
                />
                <FieldsetItem
                  color={isExpired ? 'danger' : 'none'}
                  title="Expiration Date"
                >
                  {formatDate(new Date(contractLineDetails.expirationDate))}
                </FieldsetItem>
                <FieldsetItem color="none" title="Setup">
                  <ContractLineStatus
                    isExpired={isExpired}
                    hasUnassignedAssets={hasUnassignedAssets}
                  />
                </FieldsetItem>
              </Col>
            </Row>
          </fieldset>
        </Col>
        <Col sm={4}>
          <fieldset>
            <legend>Manager Customer</legend>
            <FieldsetItem
              data={contractLineDetails.managerNetSuiteCustomerNSId || '-'}
              title="Manager NS Customer ID"
            />
            <FieldsetItem
              data={contractLineDetails.managerNetSuiteCustomerName || '-'}
              title="Manager Customer Name"
            />
          </fieldset>
        </Col>
      </Row>
    </ErrorBoundary>
  )
}

interface TagProps {
  text?: string
  color: string
  onClick?: () => void
}

const Tag: React.FC<TagProps> = ({ text, color, onClick }) => {
  if (!text) return null
  return (
    <Badge
      color={color}
      onClick={onClick}
      style={
        onClick
          ? { fontSize: 'initial', cursor: 'pointer' }
          : { fontSize: 'initial' }
      }
    >
      {text}
    </Badge>
  )
}

interface ContractLineStatusProps {
  hasUnassignedAssets: boolean
  isExpired: boolean
}

const ContractLineStatus: React.FC<ContractLineStatusProps> = ({
  hasUnassignedAssets,
  isExpired,
}) => {
  if (isExpired) return <Tag color="dark" text="Unavailable" />
  if (hasUnassignedAssets) return <Tag color="warning" text="Setup Needed" />

  return <Tag color="success" text="Complete" />
}

export default ContractLineSummary
